// Google Analytics utility functions

/**
 * Check if Google Analytics is available and properly initialized
 */
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function';
};

/**
 * Check if we're in a development or preview environment
 */
const isDevEnvironment = (): boolean => {
  try {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || 
           hostname.includes('webcontainer') ||
           hostname.includes('preview') ||
           hostname.includes('local-credentialless');
  } catch {
    return true;
  }
};

/**
 * Get the current page URL in a safe format
 */
const getSafePageUrl = (): string => {
  try {
    // Get the current URL
    const url = window.location.href;
    
    // Remove any sensitive parameters if needed
    const cleanUrl = url.split('?')[0];
    
    // Return the sanitized URL
    return cleanUrl;
  } catch {
    // Fallback to pathname if URL construction fails
    return window.location.pathname || '/';
  }
};

/**
 * Track a page view in Google Analytics
 * @param path - The page path to track
 * @param title - The page title
 */
export const trackPageView = (path: string, title: string) => {
  try {
    if (!isGtagAvailable()) {
      console.debug('Google Analytics not available');
      return;
    }

    // Skip tracking in development
    if (isDevEnvironment()) {
      console.debug('Google Analytics tracking skipped in development environment');
      return;
    }

    // Ensure path starts with a slash
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Get safe page URL
    const pageUrl = getSafePageUrl();

    window.gtag('config', 'G-TMJQCWNVDG', {
      page_path: normalizedPath,
      page_title: title,
      page_location: pageUrl,
      send_page_view: true
    });
  } catch (error) {
    console.warn('Failed to track page view:', error);
  }
};

/**
 * Track a custom event in Google Analytics
 * @param action - The event action
 * @param category - The event category
 * @param label - The event label
 * @param value - The event value (optional)
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  try {
    if (!isGtagAvailable()) {
      console.debug('Google Analytics not available');
      return;
    }

    // Skip tracking in development
    if (isDevEnvironment()) {
      console.debug('Google Analytics tracking skipped in development environment');
      return;
    }

    // Get safe page URL
    const pageUrl = getSafePageUrl();

    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      page_location: pageUrl,
      send_to: 'G-TMJQCWNVDG'
    });
  } catch (error) {
    console.warn('Failed to track event:', error);
  }
};